import { ReactNode, useEffect, useState } from 'react';
import getFormatMessage from '../../locales/get-format-message';
import { AutoTable } from '../auto-table';
import { IAutoTable } from '../auto-table/model';
import { IPagination } from '../table';
import styles from './index.module.scss';

interface IProps {
  className?: string;
  pagination?: false;
  defaultPageSize?: number;
  autoMaxHeight?: boolean;
  showTotal?: true | ((total: number, pagination: IPagination) => ReactNode);
}

export const PaginationTable = <T,>(props: IAutoTable<T> & IProps) => {
  const { tableExtension, defaultPageSize, autoMaxHeight = true } = props;
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize || 10);
  const [filterList, setFilterList] = useState<readonly T[]>(props.list || []);
  const [maxY, setMaxY] = useState<number | 'auto'>('auto');

  useEffect(() => {
    const hasFilter = !!props.configFilter;
    if (!hasFilter) {
      setFilterList(props.list || []);
    }
  }, [props.list]);

  useEffect(() => {
    setMaxY(getAutoScrollY());
  }, []);

  useEffect(() => {
    if (!props.list) {
      return;
    }
    if (props.list.length <= pageSize * (page - 1)) {
      setPage(1);
    }
  }, [props.list?.length]);

  const configFilter = props.configFilter
    ? {
        ...props.configFilter,
        onChange: (_val: any, _values: any, newList: T[]) => {
          setFilterList(newList);
          setPage(1);
          props.configFilter?.onChange?.(_val, _values, newList);
        },
      }
    : undefined;

  const getAutoScrollY = () => {
    if (!autoMaxHeight) {
      return 'auto';
    }
    const tableElements = props.className
      ? document.querySelectorAll(`.${props.className}`)
      : (document.querySelectorAll(
          '.auto-table-box'
        ) as NodeListOf<HTMLElement>);
    if (!tableElements.length) {
      return 'auto';
    }

    const validBounding = Array.from(tableElements)
      .map((table) => table.getBoundingClientRect())
      .find((bound) => bound.left);

    const paginationHeight =
      props.pagination === false && !props.showTotal ? 0 : 35;
    return innerHeight - (validBounding?.top || 0) - 65 - paginationHeight;
  };

  const getScroll = () => {
    if (!tableExtension?.scroll) {
      return {
        x: 'auto',
        maxY,
      };
    }
    return {
      ...tableExtension.scroll,
      x: tableExtension.scroll.x || 'auto',
      maxY: tableExtension.scroll.maxY || maxY,
    };
  };

  const getPagination = () => {
    const { pagination, showTotal } = props;
    const commonConfig = {
      current: page,
      total: filterList.length || 0,
      pageSize,
      showTotal: (showTotal || true) as
        | true
        | ((total: number, pagination: IPagination) => ReactNode),
    };
    if (pagination === false) {
      return undefined;
    }

    return commonConfig;
  };

  const renderTotalItems = () => {
    const { pagination, showTotal } = props;
    if (pagination === false && showTotal) {
      return (
        <div className={styles.totalItems}>
          {getFormatMessage('total_items', {
            params: {
              total: filterList.length,
            },
          })}
        </div>
      );
    }
  };

  return (
    <>
      <AutoTable
        {...props}
        tableExtension={{
          ...(props.tableExtension || {}),
          isStickyHeader: true,
          scroll: getScroll(),
          onChange: (pagination) => {
            if (!pagination) {
              return;
            }
            setPage(pagination.current);
            setPageSize(pagination.pageSize || 10);
          },
          pagination: getPagination(),
        }}
        configFilter={configFilter}
      />
      {renderTotalItems()}
    </>
  );
};
